import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AccountSwitchService, AccountSwitchEvent } from '../account-switch/account-switch.service';

@Injectable()
export class ErrorInterceptorService implements HttpInterceptor {
  // Use Injector to avoid circular dependency issues
  private _accountSwitchService: AccountSwitchService | null = null;

  constructor(private injector: Injector) {}

  private get accountSwitchService(): AccountSwitchService {
    if (!this._accountSwitchService) {
      this._accountSwitchService = this.injector.get(AccountSwitchService);
    }
    return this._accountSwitchService;
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(catchError((error) => this.handleError(error, request)));
  }

  private handleError(error: HttpErrorResponse, request: HttpRequest<any>): Observable<never> {
    // rework to handle errors from graph.microsoft.com more gracefully
    if (error.message.includes('https://graph.microsoft.com/')) {
      console.error('Graph API Error:', error.error?.error?.message);
      return throwError(() => error);
    }

    // Handle 403 Forbidden - potential "user not registered" scenario
    if (error.status === 403) {
      this.handle403Error(error, request);
    }

    if (error.error instanceof ErrorEvent) {
      console.error('An error occurred:', error.error.message);
    } else {
      // Improved error logging for backend errors
      let errorBody: string;
      try {
        errorBody = JSON.stringify(error.error);
      } catch (e) {
        // Handle circular references or other stringify errors
        errorBody = String(error.error);
      }
      const errorMessage = `Backend returned code ${error.status}, body was: ${errorBody}`;
      console.error(errorMessage);

      // Log additional details for 500 errors
      if (error.status === 500) {
        console.error('Server Error Details:', {
          url: error.url,
          status: error.status,
          statusText: error.statusText,
          error: error.error,
          message: error.message,
        });
      }
    }
    return throwError(() => error);
  }

  /**
   * Handle 403 Forbidden errors - check if it's a "user not registered" scenario
   * and trigger the account switch dialog if needed
   */
  private handle403Error(error: HttpErrorResponse, request: HttpRequest<any>): void {
    // Don't trigger for Graph API calls - those have their own error handling
    if (request.url.includes('graph.microsoft.com')) {
      return;
    }

    // Don't trigger if we're in tenant selection mode
    // This prevents dialog loops when returning to tenant selection
    if (this.accountSwitchService.isInTenantSelectionMode) {
      return;
    }

    // Determine the type of 403 error
    const eventType = this.determine403Type(error);
    const userMessage = this.extractUserMessage(error);

    const event: AccountSwitchEvent = {
      type: eventType,
      message: userMessage,
      tenantId: this.extractTenantFromError(error),
      userEmail: this.accountSwitchService.getCurrentAccountInfo()?.email,
    };

    // Trigger the account switch dialog
    this.accountSwitchService.triggerAccountSwitch(event);
  }

  /**
   * Determine the type of 403 error based on the error response
   */
  private determine403Type(error: HttpErrorResponse): AccountSwitchEvent['type'] {
    const errorBody = error.error;
    let errorString: string;

    if (typeof errorBody === 'string') {
      errorString = errorBody.toLowerCase();
    } else {
      try {
        errorString = JSON.stringify(errorBody).toLowerCase();
      } catch (e) {
        // Handle circular references or other stringify errors
        errorString = String(errorBody).toLowerCase();
      }
    }

    if (
      errorString.includes('not found') ||
      errorString.includes('not registered') ||
      errorString.includes('does not exist') ||
      errorString.includes('no user')
    ) {
      return 'user_not_registered';
    }

    if (errorString.includes('tenant') || errorString.includes('organization')) {
      return 'tenant_mismatch';
    }

    return 'access_denied';
  }

  /**
   * Extract a user-friendly message from the error response
   */
  private extractUserMessage(error: HttpErrorResponse): string {
    const errorBody = error.error;

    // Try to extract message from various error formats
    if (typeof errorBody === 'string') {
      return errorBody;
    }

    if (errorBody?.message) {
      return errorBody.message;
    }

    if (errorBody?.error?.message) {
      return errorBody.error.message;
    }

    if (errorBody?.detail) {
      return errorBody.detail;
    }

    if (errorBody?.title) {
      return errorBody.title;
    }

    // Fallback message - actual localized messages are shown in AccountSwitchDialog based on event.type
    return 'Access denied. Your account may not be registered for this tenant.';
  }

  /**
   * Try to extract tenant information from the error
   */
  private extractTenantFromError(error: HttpErrorResponse): string | undefined {
    const errorBody = error.error;

    if (typeof errorBody === 'object') {
      return errorBody?.tenantId || errorBody?.tenant || errorBody?.organizationId;
    }

    return undefined;
  }
}
